import { APIError, fetcher } from "@certn/api";
import { FileInfo } from "@certn/cascadia-react/components/upload/assets/utils";
import {
  createAjv,
  isEnabled,
  update,
  JsonSchema,
  type Layout,
  type UISchemaElement,
} from "@jsonforms/core";
import { JsonFormsContext } from "@jsonforms/react";
import { useCallback, useContext, useState } from "react";
import { FormProps } from "react-aria-components";
import { mutate } from "swr";

import {
  ApplicationSchema,
  isApplicationSchema,
  isSectionSchema,
  isUISchemaElement,
  SectionSchema,
} from "./types";

const QUICKSCREEN_BASE_PATH = "quickscreen";

export function isQuickscreenBasePath(basePath?: string): boolean {
  return basePath === QUICKSCREEN_BASE_PATH;
}

export function getCaseDetailEndpoint(
  basePath: string | undefined,
  caseId: string
): string {
  return isQuickscreenBasePath(basePath)
    ? `/api/internal/client-portal/quickscreen-case/${caseId}/`
    : `/api/internal/applicant/case/${caseId}/`;
}

export function getSection(
  name: string | undefined,
  application: ApplicationSchema
) {
  return application.elements.find((section) => section.name === name);
}

export function getInitialSectionName(application: ApplicationSchema) {
  return application.elements[0]?.name || "summary";
}

export function getEnabledSections(
  application: ApplicationSchema,
  data: unknown
) {
  return application.elements.filter((section) =>
    data ? isEnabled(section, data, "", createAjv()) : true
  );
}

export function getNextSection(
  currentSectionName: string | undefined,
  application: ApplicationSchema,
  data?: unknown
) {
  const currentSection = getSection(currentSectionName, application);

  if (!currentSection) {
    return undefined;
  }

  const enabledSections = getEnabledSections(application, data);
  const nextSection =
    application.elements[application.elements.indexOf(currentSection) + 1];
  const nextEnabledSection =
    enabledSections[enabledSections.indexOf(currentSection) + 1];

  return nextEnabledSection || nextSection;
}

export function getPreviousSection(
  currentSectionName: string | undefined,
  application: ApplicationSchema,
  data?: unknown
) {
  if (currentSectionName === "summary") {
    return application.elements.at(-1);
  }

  const currentSection = getSection(currentSectionName, application);

  if (!currentSection) {
    return undefined;
  }

  const enabledSections = getEnabledSections(application, data);

  const previousSection =
    application.elements[application.elements.indexOf(currentSection) - 1];
  const previousEnabledSection =
    enabledSections[enabledSections.indexOf(currentSection) - 1];

  return previousEnabledSection || previousSection;
}

export function parseUISchema(uischema: unknown) {
  if (!isUISchemaElement(uischema)) {
    return undefined;
  }

  return uischema;
}

export function parseDataSchema(dataSchema: unknown) {
  if (dataSchema === null || typeof dataSchema !== "object") {
    return undefined;
  }

  return dataSchema as JsonSchema | undefined;
}

export function parseApplicationSchema(uischema: unknown): ApplicationSchema {
  if (!isApplicationSchema(uischema)) {
    return { type: "Categorization", label: "", elements: [] } as const;
  }

  return uischema;
}

export function parseSectionSchema(uischema: unknown): SectionSchema {
  if (!isSectionSchema(uischema)) {
    return {
      name: "unknown-section",
      type: "Category",
      label: "Unknown Section",
      elements: [],
    } as const;
  }

  return uischema;
}

export interface SectionGroup {
  groupId: string | null;
  sections: readonly SectionSchema[];
}

export function groupSectionsByGroupId(
  elements: readonly SectionSchema[]
): SectionGroup[] {
  const groups: SectionGroup[] = [];

  for (const element of elements) {
    const groupId: string | null = element.options?.sectionGroupId || null;
    const lastGroup = groups[groups.length - 1];

    if (groupId && lastGroup?.groupId === groupId) {
      groups[groups.length - 1] = {
        ...lastGroup,
        sections: [...lastGroup.sections, element],
      };
    } else {
      groups.push({ groupId, sections: [element] });
    }
  }

  return groups;
}

export function getGroupLeader(
  currentSection: string,
  groups: SectionGroup[]
): string {
  for (const group of groups) {
    if (
      group.sections.length > 1 &&
      group.sections.some((s) => s.name === currentSection)
    ) {
      return group.sections[0].name;
    }
  }
  return currentSection;
}

export function getDisabledSectionKeys(
  uischema: ApplicationSchema,
  data: unknown,
  ajv: ReturnType<typeof createAjv>
): string[] {
  return uischema.elements
    .filter((section) => !isEnabled(section, data, "", ajv))
    .map(({ name }) => name);
}

export function getAllSectionKeys(uischema: ApplicationSchema): string[] {
  return uischema.elements.map(({ name }) => name);
}

export async function updateSection(
  id: string,
  values: { [key: string]: object },
  currentSection: string,
  validate = false,
  basePath?: string
) {
  // Case management edits the whole application in one form and has no
  // applicant section. Item controls still call this on save. Skip the
  // applicant request so the form keeps the edit until its own submit.
  if (!currentSection) {
    return;
  }

  const endpoint = isQuickscreenBasePath(basePath)
    ? `/api/internal/client-portal/quickscreen-case/${id}/sections/${currentSection}/?validate=${validate}`
    : `/api/internal/applicant/case/${id}/update-section/${currentSection}/?validate=${validate}`;
  await fetcher.submit(endpoint, values);
  await mutate(getCaseDetailEndpoint(basePath, id));
}

const setIn = (keys: string[], value: object) =>
  keys.reduceRight((acc, val) => ({ [val]: acc }), value) as {
    [key: string]: object;
  };

export function replaceOrAddItems(
  dataSection: string | string[],
  parentValues: object[],
  values: object,
  index?: number
) {
  let newDataObject;

  if (index !== undefined) {
    newDataObject = parentValues.slice();
    newDataObject.splice(index, 1, values);
  } else {
    newDataObject = parentValues?.concat(values);
  }

  return setIn(
    Array.isArray(dataSection) ? dataSection : [dataSection],
    newDataObject
  );
}

export function removeItems(
  dataSection: string | string[],
  parentValues: object[],
  indexes: number[]
) {
  const sortedIndexes = indexes.sort((a, b) => b - a);

  if (
    sortedIndexes.some((index) => index < 0 || index >= parentValues.length)
  ) {
    // Invalid index
    return;
  }

  const newDataObject = parentValues.filter(
    (_: object, indx: number) => !sortedIndexes.includes(indx)
  );

  return setIn(
    Array.isArray(dataSection) ? dataSection : [dataSection],
    newDataObject
  );
}

function generatePath(
  currentKey: string,
  currentInstancePath?: string
): string {
  if (!currentInstancePath) {
    return currentKey;
  }

  const currentPath = [...currentInstancePath.split("."), currentKey]
    .filter((path) => path !== "_")
    .filter(Boolean)
    .join(".");

  return currentPath;
}

function parseError(
  error: unknown,
  prefix = ""
): { path: string; message: string }[] {
  if (!error) return [];

  if (Array.isArray(error)) {
    return error
      .map((message, index) => {
        const path =
          typeof message === "string" ? prefix : `${prefix}.${index}`;

        return parseError(message, path);
      })
      .filter(Boolean)
      .flat();
  }

  if (typeof error === "object" && error !== null) {
    return Object.entries(error)
      .map(([key, value]) => {
        const instancePath = generatePath(key, prefix);

        return parseError(value, instancePath);
      })
      .filter(Boolean)
      .flat();
  }

  return [
    {
      path: prefix,
      message: String(error),
    },
  ];
}

export function parseSubmitErrors(
  error: unknown
): FormProps["validationErrors"] {
  if (!(error instanceof APIError)) return {};

  const errors = parseError(error.body);

  return errors.reduce((acc: Record<string, string[]>, { path, message }) => {
    const prevErrors = acc[path] || [];
    return {
      ...acc,
      [path]: [...prevErrors, message],
    };
  }, {});
}

export function findUUID(file_key: string) {
  const uuidRegex =
    /\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\b/;

  const match = file_key.match(uuidRegex);
  return match ? match[0] : "";
}

export function dataToFile(
  data?: string | null,
  subtitle?: string
): FileInfo | undefined {
  if (!data) {
    return undefined;
  }
  const foundUUID = findUUID(data);
  if (!foundUUID) {
    return undefined;
  }
  const filename = data.substring(
    data.indexOf(foundUUID) + foundUUID.length + 1
  );
  return {
    name: filename,
    subtitle,
    key: data,
  };
}

export function fileToData(file?: FileInfo) {
  return file ? file.key : null;
}

export function deepEqual(obj1: unknown, obj2: unknown) {
  // Base case: If both objects are identical, return true.
  if (obj1 === obj2) {
    return true;
  }

  // Check if both objects are objects and not null.
  if (
    typeof obj1 !== "object" ||
    typeof obj2 !== "object" ||
    obj1 === null ||
    obj2 === null
  ) {
    return false;
  }

  // Get the keys of both objects.
  const keys1 = Object.keys(obj1).filter(
    (key) => obj1[key as keyof typeof obj1] !== undefined
  );
  const keys2 = Object.keys(obj2).filter(
    (key) => obj2[key as keyof typeof obj2] !== undefined
  );

  // Check if the number of keys is the same.
  if (keys1.length !== keys2.length) {
    return false;
  }

  // Iterate through the keys and compare their values recursively.
  for (const key of keys1) {
    if (
      !keys2.includes(key) ||
      !deepEqual(obj1[key as keyof typeof obj1], obj2[key as keyof typeof obj2])
    ) {
      return false;
    }
  }

  // If all checks pass, the objects are deep equal.
  return true;
}

export function getSectionPropertyKeys(elements: UISchemaElement[]): string[] {
  const keys: string[] = [];
  for (const el of elements) {
    const scope = (el as { scope?: string }).scope;
    if (scope) {
      const match = scope.match(/#\/properties\/(.+)/);
      if (match) keys.push(match[1]);
    }
    const children = (el as Layout).elements;
    if (children) {
      keys.push(...getSectionPropertyKeys(children));
    }
  }
  return keys;
}

export function useConsentStepValidation(
  schema: JsonSchema | undefined,
  uischema: UISchemaElement,
  submit: () => void,
  data: Record<string, unknown>
) {
  const [showValidationModal, setShowValidationModal] = useState(false);

  const handleNextStep = () => {
    const elements = (uischema as Layout).elements ?? [];
    const sectionKeys = getSectionPropertyKeys(elements);

    const hasUnmetRequirements = sectionKeys.some((key) => {
      const value = data[key];
      const propSchema = schema?.properties?.[key] as
        | { type?: string }
        | undefined;
      if (!propSchema) return false;
      if (propSchema.type === "boolean") return value !== true;
      return !value;
    });

    if (hasUnmetRequirements) {
      setShowValidationModal(true);
      return;
    }
    submit();
  };

  return { showValidationModal, setShowValidationModal, handleNextStep };
}

export function useHandleChange() {
  const ctx = useContext(JsonFormsContext);
  const dispatch = ctx.dispatch;
  return useCallback(
    (path: string, value: unknown) => {
      dispatch?.(update(path, () => value));
    },
    [dispatch]
  );
}

export function checkIsString(value: unknown): string | undefined {
  // Checks that value passed to function is a string.
  return typeof value === "string" ? value : undefined;
}

export function parseStringRecord(value: unknown): Record<string, unknown> {
  // Checks that value isn't an empty object.
  if (typeof value !== "object" || value === null) {
    return {};
  }
  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value)) {
    if (typeof key === "string") {
      result[key] = val;
    } else {
      return {};
    }
  }
  return result;
}

export function getIncompleteSectionsWithNoSummary(
  uischema?: ApplicationSchema,
  incompleteSections?: SectionSchema[]
) {
  return uischema?.elements
    .filter(({ name, options }) => {
      return incompleteSections?.some(
        ({ name: incompleteName }) =>
          name === incompleteName && !options?.include_summary
      );
    })
    .map((section) => section);
}

interface FulfillWithDemoResponses {
  check_types_allowing_non_demo_responses: string[];
  only_fulfill_with_demo_responses: boolean;
}

export function getAllowRealResponses(
  fulfillWithDemoResponses: FulfillWithDemoResponses,
  checkTypes: string[]
): boolean {
  const atLeastOneCheckAllowed = checkTypes.some((check) =>
    fulfillWithDemoResponses.check_types_allowing_non_demo_responses.includes(
      check
    )
  );
  const noChecksSpecified =
    fulfillWithDemoResponses.check_types_allowing_non_demo_responses.length ===
    0;
  const isDemoResponsesOnly =
    fulfillWithDemoResponses.only_fulfill_with_demo_responses;

  return !isDemoResponsesOnly && (atLeastOneCheckAllowed || noChecksSpecified);
}

interface ApplicationProps {
  ui_schema: ApplicationSchema;
  applicant_form_data: {
    [key: string]: unknown;
  };
}

export async function getFirstIncompleteEnabledSection(
  caseId: string,
  completedSections?: string[],
  currentSection?: string,
  caseEndpoint?: string
) {
  const endpoint = caseEndpoint || `/api/internal/applicant/case/${caseId}/`;
  const { ui_schema, applicant_form_data } =
    await fetcher.request<ApplicationProps>(endpoint);
  const enabledSections = getEnabledSections(ui_schema, applicant_form_data);
  const completeAndEnabledSections = enabledSections?.filter(
    ({ name }) => !completedSections?.includes(name)
  );

  const firstIncompleteEnabledSection = completeAndEnabledSections.length
    ? completeAndEnabledSections[0].name
    : undefined;

  // maintain this behaviour to support sections that are the last section before the summary eg. demo responses section
  if (firstIncompleteEnabledSection === currentSection) {
    return undefined;
  }

  return firstIncompleteEnabledSection;
}
